import React, { useContext, useEffect, useMemo, useState } from 'react';

import * as S from './styles';
import TableBasic from '../../../../../components/newStudent/Table';
import FieldFormik from '../../../../../components/newStudent/FieldFormik';
import { Formik } from 'formik';
import {
  cleanCache,
  formatCurrency,
  formatDate,
  formatPercent,
} from '../../../../../util/common/appUtil';
import NegotiationTerm from '../NegotiationTerm';
import NegotiationSummary from '../NegotiationSummary';
import { ButtonSimple } from '../../../../../components/newStudent/Buttons';
import {
  financialServiceNewStudent,
  IInfoDebtAndNegotiationBillets,
} from '../../../../../services/newStudent/financial/financial.service';
import { IPaymentType } from '../../../../../services/newStudent/helper/helper.service';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../../../components/newStudent/ProviderNewStudent';
import { company } from '../../../../../variables';
import { INegotiationSimulation } from '../../../../../interfaces/newStudent/negotiation.interface';

interface Props {
  dataInvoice: IInfoDebtAndNegotiationBillets[];
  paymentType: IPaymentType[];
}

export default function DebtNegotiation({ dataInvoice, paymentType }: Props) {
  const { setIsLoading, student } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [readed, setReaded] = useState<boolean>();
  const [simulation, setSimulation] = useState<INegotiationSimulation | null>(
    null,
  );
  const [simulationVersion, setSimulationVersion] = useState(0);
  const [selectedPaymentType, setSelectedPaymentType] = useState<
    number | undefined
  >(0);
  const [selectedOptionsParcels, setSelectedOptionsParcels] = useState<
    number | undefined
  >(0);
  const [selectedQuantity, setSelectedQuantity] = useState(dataInvoice.length);

  const optionsPaymentType = useMemo(() => {
    return [
      { title: '[ Selecione a forma de pagamento ]', value: undefined },
      ...(paymentType.length > 0
        ? paymentType
            .filter((item) => item.description)
            .map((item) => ({
              title: item.description!.split(' ')[0],
              value: item.id,
            }))
        : []),
    ];
  }, [paymentType]);

  const option = useMemo(() => {
    return selectedPaymentType
      ? paymentType.find((item) => item.id === selectedPaymentType)
      : undefined;
  }, [selectedPaymentType, paymentType]);

  const optionsParcels = useMemo(() => {
    return [
      { title: '[ Número de parcelas ]', value: undefined },
      ...(option?.parcels
        ? option.parcels.map((item) => ({
            title: item.quantity + 'X',
            value: item.quantity,
          }))
        : []),
    ];
  }, [option]);

  useEffect(() => {
    setSelectedQuantity(dataInvoice.length);
  }, [dataInvoice]);

  const selectedIds = useMemo(
    () => dataInvoice.slice(0, selectedQuantity).map((item) => item.id),
    [dataInvoice, selectedQuantity],
  );

  const quantityOptions = useMemo(
    () =>
      dataInvoice.map((_, index) => ({
        title: index === 0 ? '1 mensalidade' : `${index + 1} mensalidades`,
        value: index + 1,
      })),
    [dataInvoice],
  );

  const itemById = useMemo(() => {
    return new Map((simulation?.items ?? []).map((item) => [item.id, item]));
  }, [simulation]);

  const paymentLabel = simulation?.isCreditCard
    ? 'Cartão de Crédito'
    : (optionsPaymentType.find((item) => item.value === selectedPaymentType)
        ?.title ?? '');

  useEffect(() => {
    if (!selectedPaymentType || !selectedOptionsParcels) {
      setSimulation(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const { result, data } =
          await financialServiceNewStudent.showNegotiationSimulation({
            id: selectedIds,
            paymentType: selectedPaymentType,
            parcel: selectedOptionsParcels,
          });
        if (!cancelled) {
          setSimulation(result ? data : null);
        }
      } catch (error) {
        if (!cancelled) {
          setSimulation(null);
        }
        throw new Error(String(error));
      } finally {
        setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedPaymentType, selectedOptionsParcels, simulationVersion, selectedIds]);

  const discountTitle = simulation
    ? `Desconto (${formatPercent(simulation.discountPercent)})`
    : 'Desconto';
  const interestTitle = simulation
    ? `Juros (${formatPercent(simulation.interestPercent)} a.m.)`
    : 'Juros';
  const fineTitle = simulation
    ? `Multa (${formatPercent(simulation.finePercent)})`
    : 'Multa';
  const increaseTitle = simulation
    ? `Acréscimo (${formatPercent(simulation.operatorFeePercent)})`
    : 'Acréscimo';

  const dataTable = {
    title: [
      {
        item: <h6 style={{ fontWeight: 'bold' }}>Referência</h6>,
        align: 'center',
      },
      {
        item: <h6 style={{ fontWeight: 'bold' }}>Vencimento</h6>,
        align: 'center',
      },
      {
        item: <h6 style={{ fontWeight: 'bold' }}>Valor</h6>,
        align: 'center',
      },
      {
        item: <h6 style={{ fontWeight: 'bold' }}>{discountTitle}</h6>,
        align: 'center',
      },
      {
        item: <h6 style={{ fontWeight: 'bold' }}>{interestTitle}</h6>,
        align: 'center',
      },
      {
        item: <h6 style={{ fontWeight: 'bold' }}>{fineTitle}</h6>,
        align: 'center',
      },
      {
        item: <h6 style={{ fontWeight: 'bold' }}>{increaseTitle}</h6>,
        align: 'center',
      },
      {
        item: <h6 style={{ fontWeight: 'bold' }}>Valor Final</h6>,
        align: 'center',
      },
    ],
    content: [
      ...(dataInvoice
        ? dataInvoice.map((item, index) => {
            if (index >= selectedQuantity) {
              return {
                invoiceReference: {
                  item: (
                    <S.ReferenceCell>
                      <h6>{item.invoiceReference}</h6>
                      {item.courseName ? (
                        <S.CourseName>{item.courseName}</S.CourseName>
                      ) : null}
                    </S.ReferenceCell>
                  ),
                  align: 'center',
                },
                dueDate: {
                  item: <h6>{formatDate(item.dueDate)}</h6>,
                  align: 'center',
                },
                value: {
                  item: <h6>{formatCurrency(item.value)}</h6>,
                  align: 'center',
                },
                discount: {
                  item: <h6>—</h6>,
                  align: 'center',
                },
                interest: {
                  item: <h6>—</h6>,
                  align: 'center',
                },
                fine: {
                  item: <h6>—</h6>,
                  align: 'center',
                },
                increase: {
                  item: <h6>—</h6>,
                  align: 'center',
                },
                total: {
                  item: <h6>—</h6>,
                  align: 'center',
                },
              };
            }
            const simulated = itemById.get(item.id);
            return {
              invoiceReference: {
                item: (
                  <S.ReferenceCell>
                    <h6>{item.invoiceReference}</h6>
                    {item.courseName ? (
                      <S.CourseName>{item.courseName}</S.CourseName>
                    ) : null}
                  </S.ReferenceCell>
                ),
                align: 'center',
              },
              dueDate: {
                item: (
                  <h6>{formatDate(simulated?.dueDate ?? item.dueDate)}</h6>
                ),
                align: 'center',
              },
              value: {
                item: (
                  <h6>{formatCurrency(simulated?.value ?? item.value)}</h6>
                ),
                align: 'center',
              },
              discount: {
                item: (
                  <h6>{formatCurrency(simulated?.discountAmount ?? 0)}</h6>
                ),
                align: 'center',
              },
              interest: {
                item: (
                  <h6>{formatCurrency(simulated?.interestAmount ?? 0)}</h6>
                ),
                align: 'center',
              },
              fine: {
                item: <h6>{formatCurrency(simulated?.fineAmount ?? 0)}</h6>,
                align: 'center',
              },
              increase: {
                item: (
                  <h6>
                    {formatCurrency(simulated?.financialIncreaseAmount ?? 0)}
                  </h6>
                ),
                align: 'center',
              },
              total: {
                item: (
                  <h6>
                    {formatCurrency(simulated?.totalAmount ?? item.value)}
                  </h6>
                ),
                align: 'center',
              },
            };
          })
        : []),
      ...(simulation
        ? [
            {
              invoiceReference: {
                item: <h6 style={{ fontWeight: 'bold' }}>TOTAL</h6>,
                align: 'center',
              },
              dueDate: {
                item: <h6 />,
                align: 'center',
              },
              value: {
                item: (
                  <h6 style={{ fontWeight: 'bold' }}>
                    {formatCurrency(simulation.debtAmount)}
                  </h6>
                ),
                align: 'center',
              },
              discount: {
                item: (
                  <h6 style={{ fontWeight: 'bold' }}>
                    {formatCurrency(simulation.discountAmount)}
                  </h6>
                ),
                align: 'center',
              },
              interest: {
                item: (
                  <h6 style={{ fontWeight: 'bold' }}>
                    {formatCurrency(simulation.interestAmount)}
                  </h6>
                ),
                align: 'center',
              },
              fine: {
                item: (
                  <h6 style={{ fontWeight: 'bold' }}>
                    {formatCurrency(simulation.fineAmount)}
                  </h6>
                ),
                align: 'center',
              },
              increase: {
                item: (
                  <h6 style={{ fontWeight: 'bold' }}>
                    {formatCurrency(simulation.financialIncreaseAmount)}
                  </h6>
                ),
                align: 'center',
              },
              total: {
                item: (
                  <h6 style={{ fontWeight: 'bold' }}>
                    {formatCurrency(simulation.totalAmount)}
                  </h6>
                ),
                align: 'center',
              },
            },
          ]
        : []),
    ],
  };

  const handleSend = async () => {
    if (!simulation || !selectedPaymentType || !selectedOptionsParcels) {
      return;
    }

    setIsLoading(true);
    try {
      const { result } = await financialServiceNewStudent.installmentsNegotiation(
        {
          id: selectedIds,
          paymentType: selectedPaymentType,
          parcel: selectedOptionsParcels,
          total: simulation.totalAmount,
          acceptedTerms: true,
        },
      );

      if (result) {
        cleanCache('dataContracts');
        localStorage.removeItem('dataStudent');
      } else {
        setReaded(false);
        setSimulationVersion((current) => current + 1);
      }
    } catch (error) {
      throw new Error(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  const validPaymentType = (id?: number) => {
    const validate = optionsPaymentType.find((item) => item.value === +id!);
    if (validate) {
      if (
        validate.title.toLowerCase().includes('pix') ||
        validate.title.toLowerCase().includes('bole')
      ) {
        setSelectedOptionsParcels(1);
        return false;
      }
    }
    return true;
  };

  return (
    <S.Container>
      <Formik
        initialValues={{
          selectQuantity: dataInvoice.length,
          selectPaymentType: undefined,
          selectOptionsParcels: undefined,
        }}
        onSubmit={() => {}}
        enableReinitialize
      >
        {({ handleChange, setFieldValue, values }) => (
          <S.DivSelect>
            {dataInvoice.length > 1 ? (
              <S.SelectField>
                <S.H6>Mensalidades a negociar:</S.H6>
                <FieldFormik
                  name="selectQuantity"
                  type="select"
                  value={values.selectQuantity}
                  onChange={(e) => {
                    handleChange(e);
                    setSelectedQuantity(+e.target.value);
                    setSimulation(null);
                    setReaded(undefined);
                  }}
                  options={quantityOptions}
                  widthSize="fullWidth"
                />
              </S.SelectField>
            ) : null}
            <S.SelectField>
              <S.H6>Forma de Pagamento: </S.H6>
              <FieldFormik
                name="selectPaymentType"
                type="select"
                value={values.selectPaymentType}
                onChange={(e) => {
                  handleChange(e);
                  setSelectedPaymentType(+e.target.value);
                  setSelectedOptionsParcels(undefined);
                  setSimulation(null);
                  setReaded(undefined);
                  setFieldValue('selectOptionsParcels', 0);
                }}
                options={optionsPaymentType}
                widthSize="fullWidth"
              />
            </S.SelectField>
            {!!selectedPaymentType &&
              validPaymentType(values.selectPaymentType) && (
                <S.SelectField>
                  <S.H6>Parcelas:</S.H6>
                  <FieldFormik
                    name="selectOptionsParcels"
                    type="select"
                    value={values.selectOptionsParcels}
                    onChange={(e) => {
                      handleChange(e);
                      setReaded(undefined);
                      setSimulation(null);
                      setSelectedOptionsParcels(+e.target.value);
                    }}
                    options={optionsParcels}
                    widthSize="fullWidth"
                  />
                </S.SelectField>
              )}
          </S.DivSelect>
        )}
      </Formik>
      <TableBasic titleNotFound=" " data={dataTable} />
      {simulation ? (
        <NegotiationSummary simulation={simulation} paymentLabel={paymentLabel} />
      ) : null}
      <S.DivSend>
        <S.CheckboxDiv>
          <S.TermButton onClick={() => setIsModalOpen(true)}>
            Termo de Aceite.
          </S.TermButton>
          {simulation ? (
            <FieldFormik
              name="readed"
              placeholder={simulation?.acceptanceText ?? ''}
              onChange={(e) => {
                setReaded(e.target.checked);
              }}
              disabled={!simulation}
              checked={readed}
              type="checkbox"
              widthSize="fullWidth"
              notLimitHeight
            />
          ) : null}
        </S.CheckboxDiv>
        <S.ButtonSlot>
          <ButtonSimple
            color="primary"
            widthSize="fullWidth"
            disabled={!simulation || !readed}
            onClick={() => handleSend()}
          >
            CONFIRMAR NEGOCIAÇÃO
          </ButtonSimple>
        </S.ButtonSlot>
      </S.DivSend>
      <NegotiationTerm
        name={student.dataStudent.name}
        company={company ?? 'Famart'}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        ableBackdropClick
        buttonLabel="CONFIRMAR NEGOCIAÇÃO"
        simulation={simulation}
        paymentLabel={paymentLabel}
      />
    </S.Container>
  );
}
